import { LoanResponse } from '@/types/api.types';
import {
  useLoans,
  useDeleteLoan,
} from '@/pages/loans/hooks/useLoans';
import { useNavigate } from 'react-router-dom';
import { UploadPortfolioModal } from '@/components/portfolio/UploadPortfolioModal';
import { LoansListHeader } from '@/pages/loans/ui/LoansListHeader';
import { LoansSearchFilters } from '@/pages/loans/ui/LoansSearchFilters';
import { DeleteConfirmationModal } from '@/pages/loans/ui/DeleteConfirmationModal';
import { LoanDetailModal } from '@/pages/loans/ui/LoanDetailModal';
import { useEffect, useState } from 'react';
import { LoansTable } from './ui/LoansTable';
import { CommonPagination } from '@/components/common/CommonPagination';
import { useQueryClient } from '@tanstack/react-query';
import { useDataChange } from '@/contexts/DataChangeContext';

const PAGE_SIZE = 20;

const LoansListPage = () => {
  // Pagination and filter state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<
    string | undefined
  >(undefined);

  const [sectorFilter, setSectorFilter] = useState<string | undefined>('all');
  const [statusFilter, setStatusFilter] = useState<string | undefined>('all');

  // UI state
  const [selectedLoan, setSelectedLoan] = useState<LoanResponse | null>(null);
  const [deleteConfirmLoan, setDeleteConfirmLoan] =
    useState<LoanResponse | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const navigate = useNavigate();

  // TanStack Query hooks
  const {
    data: loansData,
    isLoading,
    error,
  } = useLoans({
    page: currentPage,
    page_size: PAGE_SIZE,
    sectors: sectorFilter === 'all' ? undefined : sectorFilter,
    statuses: statusFilter === 'all' ? undefined : statusFilter,
    search: debouncedSearchTerm,
  });

  const deleteLoanMutation = useDeleteLoan();
  const queryClient = useQueryClient();
  const { ignoreNextChange } = useDataChange();

  // Debounce search term to avoid excessive requests while typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const normalized = searchTerm?.trim() ? searchTerm : undefined;
      setDebouncedSearchTerm(normalized);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Reset to first page when filters or search change
  useEffect(() => {
    // Only reset if not already on page 1 to avoid unnecessary re-renders
    setCurrentPage((prevPage) => (prevPage !== 1 ? 1 : prevPage));
  }, [sectorFilter, statusFilter, debouncedSearchTerm]);

  // Extract data from query result
  const loans = loansData?.items || [];
  const totalLoans = loansData?.total || 0;
  const totalPages = loansData?.total_pages || 0;
  const hasNext = loansData?.has_next || false;
  const hasPrev = loansData?.has_prev || false;

  const handleCreateNew = () => {
    navigate('/loan-form');
  };

  const handleView = (loanId: number) => {
    navigate(`/loan/${loanId}`);
  };

  const handleEdit = (loanId: number) => {
    navigate(`/loan-form/edit/${loanId}`);
  };

  const handleDelete = (loan: LoanResponse) => {
    deleteLoanMutation.mutate(loan.rp_system_id, {
      onSuccess: () => {
        setDeleteConfirmLoan(null);
        ignoreNextChange('Loans', 10000); // Ignore changes for 10 seconds after user's own action
      },
    });
  };

  const clearFilters = () => {
    setSearchTerm(undefined);
    setSectorFilter(undefined);
    setStatusFilter(undefined);
    setCurrentPage(1);
  };

  const hasActiveFilters = Boolean(
    searchTerm || sectorFilter !== 'all' || statusFilter !== 'all'
  );

  return (
    <div className="page-container" data-test-id="loans-list-page">
      <div className="page-inner" data-test-id="loans-list-inner">
        {/* Header */}
        <LoansListHeader
          onCreateNew={handleCreateNew}
          onUploadPortfolio={() => setShowUploadModal(true)}
        />

        {/* Search and Filters */}
        <LoansSearchFilters
          searchTerm={searchTerm || ''}
          sectorFilter={sectorFilter || 'all'}
          statusFilter={statusFilter || 'all'}
          onSearchChange={setSearchTerm}
          onSectorChange={setSectorFilter}
          onStatusChange={setStatusFilter}
          onClearFilters={clearFilters}
        />

        {/* Loan Results Table */}
        <LoansTable
          loans={loans}
          isLoading={isLoading}
          error={error}
          hasActiveFilters={hasActiveFilters}
          onCreateNew={handleCreateNew}
          onClearFilters={clearFilters}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={(loan) => setDeleteConfirmLoan(loan)}
          onSelect={setSelectedLoan}
        />

        {/* Pagination */}
        <CommonPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalLoans={totalLoans}
          pageSize={PAGE_SIZE}
          hasNext={hasNext}
          hasPrev={hasPrev}
          hasActiveFilters={hasActiveFilters}
          onPageChange={setCurrentPage}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          loan={deleteConfirmLoan}
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirmLoan(null)}
        />

        {/* Loan Detail Modal */}
        <LoanDetailModal
          loan={selectedLoan}
          onClose={() => setSelectedLoan(null)}
          onEdit={handleEdit}
        />
      </div>

      {/* Upload Portfolio Modal */}
      <UploadPortfolioModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={() => {
          setShowUploadModal(false);
          queryClient.invalidateQueries({ queryKey: ['loans'] });
          ignoreNextChange('Loans', 10000); // Ignore changes for 10 seconds after user's own action
        }}
      />
    </div>
  );
};

export default LoansListPage;
